import React, { memo, useEffect, useRef, useState } from 'react';

import TopNavItem from '@/components/atomic/molecules/navigation/top-nav-items/item';
import TopNavItemDivider from '@/components/atomic/molecules/navigation/top-nav-items/item/divider';
import type { TopNavItemsProps } from '@/components/atomic/molecules/navigation/top-nav-items/typings';
import TopNavItemsWrapper from '@/components/atomic/molecules/navigation/top-nav-items/wrapper';
import ViewportRender from '@/components/atomic/molecules/utils/viewport/viewport-render';
import withExposer from '@/hocs/with-exposer';
import useNavSize from '@/hooks/use-nav-size';
import { useTheme } from '@emotion/react';

const TopNavItemsComponent: React.FC<TopNavItemsProps> = ({
	$navigationItems,
	dropdownSegmentFirstIndex,
	$variant,
}) => {
	const { size } = useTheme();

	const navigationContainerRef = useRef<HTMLUListElement>(null);
	const [logoRef, setLogoRef] = useState<HTMLDivElement | null>(null);
	const { hideNavLabel, checkSpace } = useNavSize({
		containerRef: navigationContainerRef,
		offsetRef: logoRef,
	});

	useEffect(() => {
		if (!logoRef) {
			setLogoRef(
				navigationContainerRef.current?.parentElement
					?.previousSibling as HTMLDivElement,
			);
			return;
		}

		const observer = new ResizeObserver(() => checkSpace());

		observer.observe(logoRef as HTMLDivElement);

		return () => observer.disconnect();
	}, [logoRef, checkSpace]);

	if (!$navigationItems || $navigationItems.length === 0) return null;

	return (
		<TopNavItemsWrapper
			ref={navigationContainerRef}
			{...(hideNavLabel !== undefined && {
				'data-hide-nav-label': hideNavLabel,
			})}
		>
			{$navigationItems.map((sortedNavigationItem, index) => {
				return (
					<React.Fragment
						key={`${sortedNavigationItem.label}_${index}`}
					>
						<ViewportRender
							$breakpoints={{
								[size.laptop]: {
									Component: sortedNavigationItem.hasSeparator
										? TopNavItemDivider
										: undefined,
								},
							}}
						/>
						<TopNavItem
							dropdownSegmentFirstIndex={
								dropdownSegmentFirstIndex
							}
							index={index}
							$variant={$variant}
							{...sortedNavigationItem}
						/>
					</React.Fragment>
				);
			})}
		</TopNavItemsWrapper>
	);
};

const TopNavItems = memo(withExposer(TopNavItemsComponent, 'topNavItems'));

export default TopNavItems;
